import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import LogoText from "../../../media/60njktpitzg8gano2erfouonjejsbaz5.png";
import userPlanPrint from "../../../media/userPlanPrint.svg";
import dateBirthPrint from "../../../media/dateBirthPlanPrint.svg";
import dateStartPlanPrint from "../../../media/dateStartPlanPrint.svg";
import doctorPlanPrint from "../../../media/doctorPlanPrint.svg";
import personRecommend from "../../../media/magnific_a-front-and-back.png";
import checkIcon from "../../../media/check_icon1.svg";
import footerIcon1 from "../../../media/footer_icon1.svg";
import footerIcon2 from "../../../media/footer_icon2.svg";
import footerIcon3 from "../../../media/footer_icon3.svg";
import footerIcon4 from "../../../media/footer_icon4.svg";  
import infTherapy from "../../../media/infTherapy.svg";
import badTherapy from "../../../media/badTherapy.svg";
import rulesPlate from "../../../media/rules_of_plate.png";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import "./PatientPlanPrint.css";

const formatMoney = (value) => {
  const parsed = parseFloat(value ?? 0);
  if (Number.isNaN(parsed)) return "0 ₽";
  return `${parsed.toLocaleString("ru-RU")} ₽`;
};

const getAppointmentForItem = (appointments, appointmentNumber) => {
  if (!appointments) return null;
  return (
    appointments.find(
      (apt) => parseInt(apt?.appointment_number?.value, 10) === appointmentNumber,
    ) || null
  );
};

const getAppointmentStatus = (appointments, appointmentNumber) => {
  const current = getAppointmentForItem(appointments, appointmentNumber);
  return current?.status?.value === "scheduled" ? "filled" : "empty";
};

// Форматирование даты в вид dd.mm.yyyy, нулевые даты отображаем как тире
const formatPlanDateShort = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "—";
  }

  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";

  return `${day}.${month}.${year}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
};

const formatShortDate = (dateTimeStr) => {
  if (!dateTimeStr) return "—";
  const [datePart] = String(dateTimeStr).split(" ");
  const [year, month, day] = String(datePart).split("-");
  if (!year || !month || !day) return String(dateTimeStr);
  return `${day}.${month}`;
};

const injectRecommendationIconToListItems = (html, iconSrc) => {
  if (!html) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html), "text/html");

  doc.querySelectorAll("li").forEach((li) => {
    if (li.firstElementChild?.classList?.contains("patient-recommendations-li-icon")) {
      return;
    }

    const icon = doc.createElement("img");
    icon.src = iconSrc;
    icon.alt = "";
    icon.setAttribute("aria-hidden", "true");
    icon.className = "patient-recommendations-li-icon";
    li.insertBefore(icon, li.firstChild);
  });

  return doc.body.innerHTML || "";
};

// const getTreatmentStartDate = (plan) => {
//   const admissions = plan?.result?.admission || plan?.admission || null;
//   if (!admissions || typeof admissions !== "object") return "—";

//   const firstValidDate = Object.values(admissions)
//     .map((entry) => entry?.date_plan?.value || entry?.date?.value || "")
//     .map((value) => String(value).split(" ")[0])
//     .find((value) => /^\d{4}-\d{2}-\d{2}$/.test(value));

//   return firstValidDate ? formatDate(firstValidDate) : "—";
// };

const GROUP_ICON_MAP = {
  "инфузионная терапия": infTherapy,
  "инфузионная терапия (доп.)": infTherapy,
  "чекапы": infTherapy,
  "консультация": badTherapy,
  "пероральные лекарственные препараты": badTherapy,
  "пептиды": badTherapy,
  "процедуры": badTherapy,
};

const WITHOUT_APPOINTMENTS_GROUP_NAMES = new Set([
  "пероральные лекарственные препараты",
  "процедуры",
  "чекапы",
]);
const WEEK_DAYS_SHORT_KEYS = "plan.weekdaysShort";

const getWeekDaysShort = (t) =>
  String(t(WEEK_DAYS_SHORT_KEYS))
    .split(",")
    .map((day) => day.trim())
    .filter(Boolean);

const normalizeGroupName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase();

const isWithoutAppointmentsGroupByName = (name) =>
  WITHOUT_APPOINTMENTS_GROUP_NAMES.has(normalizeGroupName(name));

const getGroupAppointmentLimit = (group, plan) => {
  const groupLimit = Number(group?.count_appointment?.value);
  if (Number.isFinite(groupLimit) && groupLimit > 0) {
    return groupLimit;
  }
  const planLimit = Number(plan?.result?.count_appointment?.value);
  return Number.isFinite(planLimit) && planLimit > 0 ? planLimit : 0;
};

const getAppointmentHeaderType = (group) => {
  const groupType = Number(group?.column_type?.value);
  if (Number.isFinite(groupType) && groupType >= 0 && groupType <= 4) {
    return groupType;
  }
  return 0;
};

const getAppointmentHeaderLabel = (appointmentNumber, appointmentHeaderType, t) => {
  const index = Math.max(appointmentNumber - 1, 0);
  const weekDaysShort = getWeekDaysShort(t);
  const evenWeekDaysShort = weekDaysShort.filter((_, i) => (i + 1) % 2 === 0);
  const oddWeekDaysShort = weekDaysShort.filter((_, i) => (i + 1) % 2 !== 0);

  if (appointmentHeaderType === 2) {
    return weekDaysShort[index % weekDaysShort.length];
  }
  if (appointmentHeaderType === 3) {
    return evenWeekDaysShort[index % evenWeekDaysShort.length];
  }
  if (appointmentHeaderType === 4) {
    return oddWeekDaysShort[index % oddWeekDaysShort.length];
  }

  return t("plan.appointment", { number: appointmentNumber });
};

const getGroupIcon = (groupName) => {
  const normalizedName = String(groupName || "")
    .trim()
    .toLowerCase();
  return GROUP_ICON_MAP[normalizedName] || null;
};

const buildDynamicsChartImage = (chartData, weightLegend = "") => {
  if (!Array.isArray(chartData) || chartData.length < 2) return "";

  const chartWidth = 1100;
  const chartHeight = 340;
  const margin = { top: 20, right: 28, bottom: 96, left: 50 };
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  const valueKeys = ["weight"];
  const allValues = chartData
    .flatMap((row) => valueKeys.map((key) => Number(row?.[key])))
    .filter((num) => Number.isFinite(num));

  if (!allValues.length) return "";

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const span = Math.max(1, maxValue - minValue);
  const paddedMin = minValue - span * 0.1;
  const paddedMax = maxValue + span * 0.1;
  const range = Math.max(1, paddedMax - paddedMin);

  const xForIndex = (index) =>
    margin.left +
    (chartData.length === 1 ? 0 : (index / (chartData.length - 1)) * plotWidth);
  const yForValue = (value) =>
    margin.top + ((paddedMax - value) / range) * plotHeight;

  const toSmoothBezierPath = (points) => {
    if (!points.length) return "";
    if (points.length < 3) {
      return points
        .map((point, idx) => `${idx === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
        .join(" ");
    }

    const smoothness = 0.85;
    let path = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

    for (let i = 0; i < points.length - 1; i += 1) {
      const p0 = i > 0 ? points[i - 1] : points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i + 2 < points.length ? points[i + 2] : p2;

      const cp1x = p1.x + ((p2.x - p0.x) * smoothness) / 6;
      const cp1y = p1.y + ((p2.y - p0.y) * smoothness) / 6;
      const cp2x = p2.x - ((p3.x - p1.x) * smoothness) / 6;
      const cp2y = p2.y - ((p3.y - p1.y) * smoothness) / 6;

      path += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
    }

    return path;
  };

  const buildPath = (key) => {
    const segments = [];
    let currentSegment = [];

    chartData.forEach((row, index) => {
      const rawValue = row?.[key];
      const value = rawValue == null || rawValue === "" ? NaN : Number(rawValue);

      if (Number.isFinite(value)) {
        currentSegment.push({
          x: xForIndex(index),
          y: yForValue(value),
        });
      } else if (currentSegment.length) {
        segments.push(currentSegment);
        currentSegment = [];
      }
    });

    if (currentSegment.length) {
      segments.push(currentSegment);
    }

    if (!segments.length) return "";

    return segments
      .map((segment) => toSmoothBezierPath(segment))
      .filter(Boolean)
      .join(" ");
  };

  const tickCount = 5;
  const gridLines = Array.from({ length: tickCount + 1 }, (_, idx) => {
    const ratio = idx / tickCount;
    const y = margin.top + ratio * plotHeight;
    const value = paddedMax - ratio * range;
    return `
      <line x1="${margin.left}" y1="${y.toFixed(2)}" x2="${chartWidth - margin.right}" y2="${y.toFixed(2)}" stroke="#e7edf8" stroke-width="1" />
      <text x="${margin.left - 8}" y="${(y + 4).toFixed(2)}" text-anchor="end" font-size="16" fill="#667085">${Math.round(value)}</text>
    `;
  }).join("");

  const xLabelStep = Math.max(1, Math.ceil(chartData.length / 8));
  const xLabels = chartData
    .map((row, index) => {
      if (index % xLabelStep !== 0 && index !== chartData.length - 1) return "";
      const x = xForIndex(index);
      const label = String(row?.dateLabel || "");
      return `<text x="${x.toFixed(2)}" y="${(chartHeight - margin.bottom + 28).toFixed(2)}" text-anchor="middle" font-size="16" fill="#667085">${label}</text>`;
    })
    .join("");

  const weightPath = buildPath("weight");
  const legendWidth = 180;
  const legendX = (chartWidth - legendWidth) / 2;
  const legendY = chartHeight - 18;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${chartWidth} ${chartHeight}" width="${chartWidth}" height="${chartHeight}">
  <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" fill="#ffffff" />
  ${gridLines}
  <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${chartHeight - margin.bottom}" stroke="#98a2b3" stroke-width="1" />
  <line x1="${margin.left}" y1="${chartHeight - margin.bottom}" x2="${chartWidth - margin.right}" y2="${chartHeight - margin.bottom}" stroke="#98a2b3" stroke-width="1" />
  ${xLabels}
  ${weightPath ? `<path d="${weightPath}" fill="none" stroke="#2f6fed" stroke-width="2.4" />` : ""}
  <g transform="translate(${legendX.toFixed(2)}, ${legendY})">
    <line x1="0" y1="0" x2="18" y2="0" stroke="#2f6fed" stroke-width="2.4" />
    <text x="23" y="4" font-size="16" fill="#121734">${weightLegend}</text>  </g>
</svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

function PatientPlanPrint() {
  const { t } = useTranslation("patient");
  const { patientId, planId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [dynamics, setDynamics] = useState([]);
  const [printSections, setPrintSections] = useState({
    recommendations: true,
    recommendationsImage: true,
    rulesPlate: true,
    plan: true,
    dynamics: true,
  });

  useEffect(() => {
    const fetchPlan = async () => {
      setLoading(true);
      setError("");

      try {
        const parsedPlanId = Number(planId);
        const currentPlanId =
          Number.isFinite(parsedPlanId) && parsedPlanId > 0 ? parsedPlanId : 0;

        const [planData, appointmentsData, dynamicsData] = await Promise.all([
          GetData({
            module: "patient",
            block: "treatment_plan",
            id_patient: patientId,
            id_plan: currentPlanId,
          }),
          GetData({
            module: "patient",
            block: "list_appointments",
            id_patient: patientId,
          }),
          GetData({
            module: "patient",
            block: "indicators",
            id_patient: patientId,
          }),
        ]);
        const recommendationsData = await GetData({
          module: "patient",
          block: "recommendations",
          id_patient: patientId,
          id_plan: currentPlanId,
          id_recommendation: 0,
        });

        setPlan(planData);
        setAppointments(appointmentsData);
        setRecommendations(recommendationsData);
        if (Array.isArray(dynamicsData?.result)) {
          setDynamics(dynamicsData.result);
        } else if (dynamicsData?.result) {
          setDynamics([dynamicsData.result]);
        } else {
          setDynamics([]);
        }
        console.log("PLAN:", planData);
        console.log("APPOINTMENTS:", appointmentsData);
        console.log("RECOMMENDATIONS:", recommendationsData);
        console.log("DYNAMICS:", dynamicsData);
      } catch (err) {
        setError(err?.message || t("plan.print.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [patientId, planId, t]);

  const courseIdToColor = useMemo(() => {
    const map = {};
    (plan?.result?.courses || []).forEach((course) => {
      const id = course?.id?.value;
      const color = course?.color?.value;
      if (id && color) map[id] = color;
    });
    return map;
  }, [plan?.result?.courses]);
  const courseIdToName = useMemo(() => {
    const map = {};
    (plan?.result?.courses || []).forEach((course) => {
      const id = course?.id?.value;
      const name = course?.name?.value;
      if (id && name) map[id] = name;
    });
    return map;
  }, [plan?.result?.courses]);
  const recommendationsList = useMemo(() => {
    const result = recommendations?.result;
    if (Array.isArray(result)) {
      return result.filter(
        (item) => item?.title?.value || item?.description?.value,
      );
    }
    if (result?.title?.value || result?.description?.value) {
      return [result];
    }
    return [];
  }, [recommendations]);

  const recommendationsWithIcons = useMemo(
    () =>
      recommendationsList.map((item) => ({
        id: item?.id?.value,
        title: item?.title?.value || t("plan.buttons.recommendations"),
        html: injectRecommendationIconToListItems(
          item?.description?.value || "",
          checkIcon,
        ),
      })),
    [recommendationsList, t],
  );
  const sortedDynamics = useMemo(() => {
    if (!Array.isArray(dynamics) || !dynamics.length) return [];
    return [...dynamics].sort((a, b) => {
      const ad = a?.date_created ? Date.parse(a.date_created) : NaN;
      const bd = b?.date_created ? Date.parse(b.date_created) : NaN;
      if (!Number.isNaN(ad) && !Number.isNaN(bd)) return bd - ad;
      return 0;
    });
  }, [dynamics]);
  const chartData = useMemo(
    () =>
      sortedDynamics
        .slice()
        .reverse()
        .map((d) => ({
          id: d?.id ?? d?.date_created,
          date_created: d?.date_created,
          dateLabel: formatShortDate(d?.date_created),
          weight:
            d?.weight != null && d?.weight !== ""
              ? Number(d.weight)
              : null,
        }))
        .filter((row) => row.date_created),
    [sortedDynamics],
  );
  const dynamicsChartImage = useMemo(
    () => buildDynamicsChartImage(chartData, t("plan.print.chartWeightLegend")),
    [chartData, t],
  );
  const hasRecommendations = recommendationsWithIcons.length > 0;
  const hasDynamics = sortedDynamics.length > 0;

  if (loading) return <div className="plan-print-loading">{t("plan.loading")}</div>;
  if (error)
    return (
      <div className="plan-print-error">
        {t("plan.print.errorPrefix", { message: error })}
      </div>
    );
  if (!plan?.result || Array.isArray(plan.result)) {
    return <div className="plan-print-empty">{t("plan.print.notFound")}</div>;
  }

  const patientFio = appointments?.result?.fio?.value || "—";
  const doctorFio = appointments?.result?.doc_name?.value || "—";
  const birthDate = formatDate(appointments?.result?.birthDate?.value || "—");
  const treatmentStartDate = formatDate(plan?.result?.date_start?.value || "—");;
  const cardNumber = appointments?.result?.cart_patient?.value || "—";

  const getAdmissionInfoForGroupAppointment = (group, appointmentNumber) => {
    if (!plan?.result?.admission || !group?.items) return null;

    for (const item of group.items) {
      const appointment = (item?.appointments || []).find(
        (apt) => parseInt(apt?.appointment_number?.value, 10) === appointmentNumber,
      );
      if (!appointment) continue;

      const admissionId = appointment?.id_admission?.value;
      if (!admissionId || String(admissionId) === "0") continue;

      const admissionInfo = plan.result.admission[admissionId];
      if (admissionInfo) return admissionInfo;
    }

    return null;
  };
  const togglePrintSection = (sectionName) => {
    setPrintSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };


  
  return (
    <div className="patient-plan-print-page">
      <div className="plan-print-controls no-print">
        <Link
          to={`/patient/${patientId}/plan/${planId}/print-other`}
          className="button-default light">
          {t("plan.print.newFormat")}
        </Link>
        <button
          type="button"
          className="button-default light"
          onClick={() => window.print()}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              width="20"
              height="20"
              style={{
                fill: "currentColor",
                transform: "scale(1.2)",
                transformOrigin: "center",
              }}>
              <path d="M19 8H5a3 3 0 0 0-3 3v4h4v4h12v-4h4v-4a3 3 0 0 0-3-3zm-3 9H8v-5h8v5zm3-7a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM18 3H6v4h12V3z" />
            </svg>
            {t("plan.buttons.print")}
          </span>
        </button>
        <Link
          to={`/patient/${patientId}/plan/${planId}`}
          className="button-default normal">
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              width="20"
              height="20"
              style={{ color: "currentColor" }}>
              <path
                d="M9 14L4 9l5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 9h11a4 4 0 1 1 0 8H9"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("plan.print.back")}
          </span>
        </Link>

      </div>
      <div className="patient-plan-print-intro-upper">
        <div className="patient-plan-print-intro-upper-left">
          <div className="patient-plan-print-intro-title">{t("plan.print.introTitle")}<br /> {t("plan.print.introSubtitle")}</div>
        </div>
        <div className="patient-plan-print-intro-upper-right">
          <img src={LogoText} alt={t("plan.print.logoAlt")} className="patient-plan-print-logo" />
        </div>
      </div>


      <div className="patient-plan-print-intro">
        <div className="patient-plan-print-intro-left">
          <div className="patient-plan-fio-block">
            <div className="patient-plan-fio-img">
              <img src={userPlanPrint} alt={t("plan.print.patientAlt")} className="patient-plan-print-intro-icon" />
            </div>
            <div className="patient-plan-print-intro-line-block">
              <div className="patient-plan-print-intro-line">
                <strong>{t("plan.print.patient")}</strong> 
                <div className="patient-plan-print-patient-block">{patientFio}</div>
              </div>
              <div className="patient-plan-print-intro-line date">
                <strong>{t("plan.print.birthDate")}</strong> 
                <div className="patient-plan-print-patient-block">{birthDate}</div>
              </div>
              <div className="patient-plan-print-intro-line date">
                <strong>{t("plan.header.startDate")}</strong> 
                <div className="patient-plan-print-patient-block">{treatmentStartDate}</div>
              </div>
              <div className="patient-plan-print-intro-line date">
                <strong>{t("plan.print.cardNumber")}</strong> 
                <div className="patient-plan-print-patient-block">{cardNumber}</div>
              </div>
            </div>

          </div>


          <div className="vertical-line"></div>

          <div className="patient-plan-doctor-block">
            <div className="patient-plan-doctor-block-left">
              <div className="patient-plan-doctor-img">
                <img src={doctorPlanPrint} alt={t("plan.print.doctorAlt")} className="patient-plan-print-intro-icon" />
              </div>
              <div className="patient-plan-print-intro-line">
                <strong>{t("plan.print.doctorFio")}</strong> 
                <div className="patient-plan-print-patient-block">{doctorFio}</div>
              </div>
            </div>
            <div className="patient-plan-doctor-block-right">
              <div className="patient-plan-print-intro-line signature">
                  <strong>{t("plan.print.signature")}</strong>
                  <span className="patient-plan-print-signature-line" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="plan-print-block-toggle no-print">
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.recommendations}
            onChange={() => togglePrintSection("recommendations")}
          />
          <span>{t("plan.buttons.recommendations")}</span>
        </label>
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.recommendationsImage}
            onChange={() => togglePrintSection("recommendationsImage")}
            disabled={!printSections.recommendations}
          />
          <span>{t("plan.print.toggleRecommendationsImage")}</span>
        </label>
        <label className="plan-print-section-toggle">
          <input
            type="checkbox"
            checked={printSections.rulesPlate}
            onChange={() => togglePrintSection("rulesPlate")}
            disabled={!printSections.recommendations}
          />
          <span>{t("plan.print.toggleRulesPlate")}</span>
        </label>
      </div>
      {printSections.recommendations && (
        hasRecommendations ? (
          <div className="patient-recommendations-print-container">
            <div className="patient-recommendations-print-left">
              {recommendationsWithIcons.map((item, index) => (
                <div key={item.id || index} className="patient-recommendations-print-block">
                  <div className="patient-recommendations-print-header">
                    <div className="patient-plan-print-section-title">
                      {item.title}
                    </div>
                  </div>
                  <div className="patient-recommendations-print-body">
                    <div dangerouslySetInnerHTML={{ __html: item.html }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="patient-recommendations-print-right">
              {printSections.recommendationsImage && (
                <div className="patient-recommendations-img">
                  <img src={personRecommend} alt={t("plan.print.recommendationsAlt")} className="patient-recommendations-print-intro-icon" />
                </div>
              )}
              {printSections.rulesPlate && (
                <div className="patient-rules-plate-print">
                  <img src={rulesPlate} alt={t("plan.print.rulesPlateAlt")} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="patient-recommendations-print-container">
            <div className="patient-recommendations-print-left">
              <div className="patient-recommendations-print-header">
                <div className="patient-plan-print-section-title">{t("plan.buttons.recommendations")}</div>
              </div>
              <div className="patient-recommendations-print-body">
                <div>{t("plan.print.recommendationsNotFound")}</div>
              </div>
            </div>
          </div>
        )
      )}

    <div className="plan-print-block-toggle no-print">
      <label className="plan-print-section-toggle">
        <input
          type="checkbox"
          checked={printSections.plan}
          onChange={() => togglePrintSection("plan")}
        />
        <span>{t("plan.print.togglePlan")}</span>
      </label>
    </div>
    {printSections.plan && (
    <div className="patient-plan-print-container">
        <div className="patient-plan-print-header">
          <div className="patient-plan-print-section-title">
            {plan?.result?.name?.value || t("plan.print.planFallback")}
          </div>
          {/* <div className="patient-plan-print-meta">
            <span>
              Общая стоимость: <strong>{formatMoney(plan?.result?.total_calculated_price?.value)}</strong>
            </span>
          </div> */}
        </div>

        {(plan?.result?.groups || []).map((group) => {
          const groupName =
            getGroupTypeLabel(t, group) || t("plan.print.groupFallback");
          const groupIcon = getGroupIcon(group?.name?.value);
          const isWithoutAppointmentsGroup = isWithoutAppointmentsGroupByName(group?.name?.value);
          const groupAppointmentLimit = getGroupAppointmentLimit(group, plan);
          const appointmentHeaderType = getAppointmentHeaderType(group);
          const groupAppointmentNumbers = isWithoutAppointmentsGroup
            ? []
            : Array.from({ length: groupAppointmentLimit }, (_, index) => index + 1);
          const appointmentColumnWidth =
            groupAppointmentNumbers.length > 0 ? `${60 / groupAppointmentNumbers.length}%` : "60%";

          return (
            <section key={group?.id?.value || group?.name?.value} className="patient-plan-print-group">
            <table>
              <colgroup>
                <col style={{ width: "40%" }} />
                {isWithoutAppointmentsGroup ? (
                  <col style={{ width: "60%" }} />
                ) : (
                  groupAppointmentNumbers.map((number) => (
                    <col key={`col-${number}`} style={{ width: appointmentColumnWidth }} />
                  ))
                )}
              </colgroup>
              <thead>
                <tr>
                  <th className="appointment-hdr-title">
                    <h2 className="appointment-hdr-title-content">
                      {groupIcon && (
                        <img
                          src={groupIcon}
                          alt=""
                          aria-hidden="true"
                          className="appointment-hdr-title-icon"
                        />
                      )}
                      {groupName}
                    </h2>
                  </th>
                  {isWithoutAppointmentsGroup ? (
                    <th className="appointment-header">{t("plan.group.composition")}</th>
                  ) : (
                    groupAppointmentNumbers.map((number) => (
                      <th className="appointment-header" key={number}>
                        {getAppointmentHeaderLabel(number, appointmentHeaderType, t)}
                      </th>
                    ))
                  )}
                </tr>
                {/* <tr>
                  <th className="appointment-hdr-title"></th>
                  {appointmentNumbers.map((number) => {
                    const admissionInfo = getAdmissionInfoForGroupAppointment(group, number);
                    const planDateValue = admissionInfo?.date_plan?.value;

                    return <th className="appointment-header" key={`date-${number}`}>{formatPlanDateShort(planDateValue)}</th>;
                  })}
                </tr> */}
              </thead>
              <tbody>
                {(() => {
                  const sortedItems = [...(group?.items || [])].sort((a, b) => {
                    const idA = parseInt(a?.id_treatment_plan_course?.value || 0, 10);
                    const idB = parseInt(b?.id_treatment_plan_course?.value || 0, 10);
                    return idB - idA;
                  });
                  const colSpan = 1 + (isWithoutAppointmentsGroup ? 1 : (groupAppointmentNumbers?.length || 0));
                  const courseIdsWithItems = new Set(
                    sortedItems
                      .map((it) => it?.id_treatment_plan_course?.value)
                      .filter((id) => id != null && String(id).trim() !== "" && String(id) !== "0"),
                  );

                  let lastCourseId = null;

                  return sortedItems.map((item) => {
                    const courseId = item?.id_treatment_plan_course?.value;
                    const isCourse = Boolean(courseId) && String(courseId) !== "0";
                    const courseColorStyle =
                      isCourse && courseIdToColor[courseId]
                        ? { backgroundColor: courseIdToColor[courseId] }
                        : undefined;

                    const courseNameRaw = isCourse
                      ? String(courseIdToName[courseId] ?? "").trim()
                      : "";
                    const courseName =
                      courseNameRaw && courseNameRaw !== "0" ? courseNameRaw : "";

                    const shouldShowCourseTitle =
                      isCourse &&
                      courseName &&
                      courseIdsWithItems.has(courseId) &&
                      courseId !== lastCourseId;
                    if (shouldShowCourseTitle) lastCourseId = courseId;

                    return (
                      <React.Fragment key={item?.id?.value || item?.name?.value}>
                        {shouldShowCourseTitle && (
                          <tr className="course-title-row">
                            <td className="service-name" colSpan={colSpan} style={courseColorStyle}>
                              <div className="course-title-name">{t("plan.group.program", { name: courseName })}</div>
                            </td>
                          </tr>
                        )}
                        <tr className={isCourse ? "course-service-row" : undefined}>
                          <td style={courseColorStyle}>
                            <div className="service-name">{item?.name?.value || "—"}</div>
                            {!isWithoutAppointmentsGroup && item?.composition?.value ? (
                              <div className="service-composition">{item.composition.value}</div>
                            ) : null}
                            {Array.isArray(item?.subitems) && item.subitems.length > 0 ? (
                              <div className="service-subitems">
                                {item.subitems.map((subitem, index) => (
                                  <div className="service-subitem" key={subitem?.id?.value || index}>
                                    <div className="mb-1">{subitem?.name?.value || "—"}</div>
                                    {subitem?.composition?.value ? (
                                      <div className="mb-1">{subitem?.composition?.value}</div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            {/* <div className="service-price"><div className="my-1">{item?.amount_base_total?.value ? formatMoney(item.amount_base_total.value) : "—"}</div></div> */}
                          </td>
                          {isWithoutAppointmentsGroup ? (
                            <td style={courseColorStyle}>
                              <div className="service-composition">{item?.composition?.value || "—"}</div>
                            </td>
                          ) : (
                            groupAppointmentNumbers.map((number) => {
                              const appointment = getAppointmentForItem(item?.appointments, number);
                              const appointmentComment = String(
                                appointment?.comment?.value ?? "",
                              ).trim();
                              const appointmentStatus = getAppointmentStatus(
                                item?.appointments,
                                number,
                              );
                              const hasCommentOnFilledAppointment =
                                appointmentStatus === "filled" && Boolean(appointmentComment);

                              return (
                                <td key={number} style={courseColorStyle}>
                                  <div className="appointment-cell-content">
                                    <span
                                      className={`appointment-indicator ${appointmentStatus} ${hasCommentOnFilledAppointment ? "with-comment" : ""} print`}
                                    />
                                    {appointmentComment ? (
                                      <div className="appointment-comment-text">
                                        {appointmentComment}
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                              );
                            })
                          )}
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
            </section>
          );
        })}
    </div>
    )}
    <div className="plan-print-block-toggle no-print">
      <label className="plan-print-section-toggle">
        <input
          type="checkbox"
          checked={printSections.dynamics}
          onChange={() => togglePrintSection("dynamics")}
          disabled={!hasDynamics}
        />
        <span>{t("plan.print.toggleDynamics")}</span>
      </label>
    </div>
    {printSections.dynamics && hasDynamics && (
      <div className="patient-dynamics-print-container">
        <div className="patient-plan-print-section-title">{t("plan.print.dynamicsTitle")}</div>

        {chartData.length >= 2 && (
          <div className="patient-dynamics-print-chart">
            {dynamicsChartImage ? (
              <img
                src={dynamicsChartImage}
                alt={t("plan.print.dynamicsChartAlt")}
                className="patient-dynamics-print-chart-image"
              />
            ) : null}
          </div>
        )}

        <div className="patient-dynamics-print-table-wrap patient-plan-print-group">
          <table className="patient-dynamics-print-table">
            <thead>
              <tr>
                <th>{t("plan.print.dynamicsDate")}</th>
                <th>{t("plan.print.dynamicsBp")}</th>
                <th>{t("plan.print.dynamicsWeight")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedDynamics.map((row) => (
                <tr key={row?.id ?? row?.date_created}>
                  <td>{formatDate(row?.date_created)}</td>
                  <td>{row?.blood_pressure ?? "—"}</td>
                  <td>{row?.weight ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="patient-plan-print-footer">
      <div className="patient-plan-print-ftr">
        <div className="patient-plan-print-ftr1">
          <div className="patient-plan-print-footer-item">
            <div className="patient-plan-print-footer-item-image">
              <img src={footerIcon1} alt="" aria-hidden="true" className="patient-plan-print-footer-icon" />
            </div>
            <div className="patient-plan-print-footer-item-content">
              <div className="patient-plan-print-footer-item-title">{t("plan.print.footer1Title")}</div>
              <div className="patient-plan-print-footer-item-text">{t("plan.print.footer1Text")}</div>
            </div>
          </div>
        </div>
        <div className="patient-plan-print-ftr2">
          <div className="patient-plan-print-footer-item">
            <div className="patient-plan-print-footer-item-image">
              <img src={footerIcon2} alt="" aria-hidden="true" className="patient-plan-print-footer-icon" />
            </div>
            <div className="patient-plan-print-footer-item-content">
              <div className="patient-plan-print-footer-item-title">{t("plan.print.footer2Title")}</div>
              <div className="patient-plan-print-footer-item-text">{t("plan.print.footer2Text")}</div>
            </div>
          </div>
        </div>
        <div className="patient-plan-print-ftr3">
          <div className="patient-plan-print-footer-item">
            <div className="patient-plan-print-footer-item-image">
              <img src={footerIcon3} alt="" aria-hidden="true" className="patient-plan-print-footer-icon" />
            </div>
            <div className="patient-plan-print-footer-item-content">
              <div className="patient-plan-print-footer-item-title">{t("plan.print.footer3Title")}</div>
              <div className="patient-plan-print-footer-item-text">{t("plan.print.footer3Text")}</div>
            </div>
          </div>
        </div>
        <div className="patient-plan-print-ftr4">
          <div className="patient-plan-print-footer-item">
            <div className="patient-plan-print-footer-item-image">
              <img src={footerIcon4} alt="" aria-hidden="true" className="patient-plan-print-footer-icon" />
            </div>
            <div className="patient-plan-print-footer-item-content">
              <div className="patient-plan-print-footer-item-title">{t("plan.print.footer4Title")}</div>
              <div className="patient-plan-print-footer-item-text">{t("plan.print.footer4Text")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>



    </div>
  );  
}

export default PatientPlanPrint;
